import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { userAddressSchema, type UserAddressFormData } from "@/lib/validationSchemas"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormErrorMessage } from "@/components/ui/form"
import { AddressFields } from "./AddressFields"
import { Form } from "@/components/ui/form"
import { useLanguage } from "@/hooks/useLanguage"

interface AddressFormProps {
  initialData?: any
  onSubmit: (data: UserAddressFormData) => void
  onCancel: () => void
  showFooter?: boolean
}

export const AddressForm: React.FC<AddressFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  showFooter = true,
}) => {
  const { t } = useLanguage()
  const text = t.savedAddresses

  const form = useForm<UserAddressFormData>({
    resolver: zodResolver(userAddressSchema),
    defaultValues: initialData || {
      isDefault: false,
      address: { state: "" },
    },
  })

  const { handleSubmit, reset, setValue, watch, register, control, formState: { errors } } = form

  useEffect(() => {
    if (initialData) {
      reset(initialData)
    }
  }, [initialData, reset])

  return (
    <Form {...form}>
      <form id="address-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="receiverName">{text.receiver}</Label>
            <Input id="receiverName" {...register("receiverName")} />
            <FormErrorMessage>{errors.receiverName?.message as string}</FormErrorMessage>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{text.phone}</Label>
            <Input id="phone" {...register("phone")} />
            <FormErrorMessage>{errors.phone?.message as string}</FormErrorMessage>
          </div>
        </div>

        <div className="border-t pt-6 border-border">
          <AddressFields
            register={register}
            errors={errors}
            control={control}
            setValue={setValue}
            prefix="address"
          />
        </div>

        <div className="flex items-center space-x-2 border-t border-foreground/5 pt-4">
          <Checkbox
            id="isDefault"
            onCheckedChange={(checked) => setValue("isDefault", checked === true)}
            checked={watch("isDefault")}
            className="rounded-sm border-input bg-background data-[state=checked]:border-primary data-[state=checked]:bg-primary"
          />
          <label
            htmlFor="isDefault"
            className="cursor-pointer text-xs font-bold uppercase tracking-widest text-muted-foreground"
          >
            {text.setDefault}
          </label>
        </div>

        {showFooter && (
          <div className="flex justify-end gap-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="font-display tracking-widest"
            >
              {text.cancel}
            </Button>
            <Button
              type="submit"
              className="bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
            >
              {text.save}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

